import axios from "axios";
import React from "react";
import _compact from "lodash/compact";
import { Outlet, useOutletContext } from "react-router-dom";
import { connect } from "react-redux";
import { compose } from "redux";
import {
  withRouter,
  BreadcrumbBar,
  IBreadcrumbBarItem,
  IOption,
  ITabProps,
  myAccountLink,
  Tabs,
  RouterProps,
} from "component-library";
import { AccountType } from "shared-types";
import { i18n } from "i18n";
import { checkIsYp } from "shared-auth";
import { FacetCategory, IFacetGroup } from "shared/models/facet";
import {
  IAgentJobSettings,
  IPerformerJobSettings,
} from "shared/models/settings";

import { MapDispatchToProps } from "shared/types/utilities";
import PageKey from "config/PageKey";

import { IApplicationState } from "store";
import { getFacets } from "store/facet/facet.actions";
import { FacetStoreKey, GetFacets } from "store/facet/facet.types";
import { getJobsPage } from "store/page/page.actions";
import { GetJobsPage } from "store/page/page.types";
import { setJobsFeedData } from "store/jobsFeed/jobsFeed.actions";
import { SetJobsFeedData, IJobsFeedState } from "store/jobsFeed/jobsFeed.types";
import { getPageTranslation } from "store/translation/translation.actions";
import { GetPageTranslation } from "store/translation/translation.types";

import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";
import { HidePopup, SetLoading, ShowPopup } from "store/ui/ui.types";

import { IJobFilterParams, JobItemReadStatus } from "models/api";
import { IClientAlertListResponse } from "models/clientAlert";
import jobService from "services/api/job/jobService";
import selfTapeRequestNotificationService from "services/api/selfTapeRequest/selfTapeRequestNotificationService";
import suggestionsService from "services/api/suggestions/suggestionsService";

import { IClientAlertsRequestParams } from "services/api/suggestions/suggestionsService.types";

import {
  IWithJobsFilterOptions,
  setupFilterConfig,
} from "hoc/withJobsFilter/withJobsFilter";
import {
  DEFAULT_VALUES,
  FilterType,
} from "hoc/withJobsFilter/withJobsFilter.config";

import { FilterModelToJobFilterParamsConverter } from "hoc/withJobsFilter/withJobsFilter.converters";
import withPageData, {
  ILoaders,
  IWithPageDataProps,
  mapStateToWithPageDataProps,
} from "hoc/withPageData";
import AllOpportunitiesPage, {
  withJobsFilterOptions as allOpportunitiesWithJobsFilterOptions,
} from "pages/JobsPage/AllOpportunitiesPage/AllOpportunitiesPage";
import AllPaidPage, {
  withJobsFilterOptions as allPaidWithJobsFilterOptions,
} from "pages/JobsPage/AllPaidPage/AllPaidPage";
import MySuggestions from "pages/MySuggestions";
import { getFilterSearch, JobsPageTab } from "pages/JobsPage/JobsPage.config";

import {
  getLastPathnameElement,
  prepareBreadcrumbs,
  prepareCommonTexts,
  prepareTabs,
} from "pages/JobsPage/JobsPage.helpers";
import MatchingMyFilterPage, {
  withJobsFilterOptions as matchingMyFilterWithJobsFilterOptions,
} from "pages/JobsPage/MatchingMyFilterPage/MatchingMyFilterPage";
import ClientAlertsPage from "pages/JobsPage/ClientAlertsPage/ClientAlertsPage";
import SelfTapeRequestsPage from "pages/JobsPage/SelfTapeRequestNotificationsPage/SelfTapeRequestNotificationsPage";
import getMySuggestionsCount from "pages/JobsPage/shared/getMySuggestionsCount";
import { DEFAULT_JOBS_FEEED } from "shared/helpers/common";
import {
  ALL_OPPORTUNITIES_PATH,
  ALL_PAID_PATH,
  BASE_PATH,
  CLIENT_ALERTS_PATH,
  MY_FILTERS_PATH,
  MY_SUGGESTIONS_PATH,
  SELF_TAPE_PATH,
} from "config/common";
import {
  sendJobsfeedUsageHeapEventWithUserPrefix,
  JobsFeedUsageEvents,
} from "shared-services";
import "./JobsPage.scss";

export interface IJobsPageActions {
  getPageTranslation: GetPageTranslation;
  setLoading: SetLoading;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  getJobsPage: GetJobsPage;
  getFacets: GetFacets;
  setJobsFeedData: SetJobsFeedData;
}

export interface IJobsPageCommonTexts {
  title: string;
  searchPlaceholder: string;
  sortingOptions: IOption<string>[];
  filterOptions: IOption<string>[];
  extendedFilterOptions: {
    openClosedRole: IOption<string>[];
  };
  placeholders: {
    productionType: string;
    contractType: string;
    dateSent: string;
    sender: string;
    playingAge: string;
    playingAgeFromLabel: string;
    playingAgeFromPlaceholder: string;
    playingAgeToLabel: string;
    playingAgeToPlaceholder: string;
    playingAgeEmptyLabel: string;
    yearLabel: string;
    yearsLabel: string;
    playingAgeOver: string;
    playingAgeUnder: string;
    locationLabel: string;
    searchLocationLabel: string;
    searchLocationPlaceholder: string;
  };
}

export interface IJobsPageStoreProps {
  breadcrumbs: IBreadcrumbBarItem[];
  tabs: { id: JobsPageTab; text: string }[];
  commonTexts: IJobsPageCommonTexts;
  pageTranslationLocale: string;
  facetLocale: string;
  productionTypes: IFacetGroup[];
  contractTypes: IFacetGroup[];
  jobSettings: IPerformerJobSettings | IAgentJobSettings;
  jobsFeed: IJobsFeedState;
  getJobCounts(
    filters: IJobFilterParams[],
    abortController?: AbortController
  ): Promise<number[]>;
  getClientAlerts(
    params: IClientAlertsRequestParams,
    abortController?: AbortController
  ): Promise<IClientAlertListResponse>;
  getSelfTapeCount(abortController?: AbortController): Promise<number>;
}

export type Counters = Record<JobsPageTab, number | undefined>;

export interface IJobsPageInjectedProps {
  userType: AccountType;
  userId: string;
  locale: string;
  commonTexts: IJobsPageCommonTexts;
  pageTranslationLocale: string;
  facetLocale: string;
  productionTypes: IFacetGroup[];
  contractTypes: IFacetGroup[];
  jobSettings: IPerformerJobSettings | IAgentJobSettings;
  refreshCounters(): void;
  restoreScrollPosition: number | null;
  page: number;
}

export type JobsPageOutletContext = IJobsPageInjectedProps & {
  router: RouterProps["router"];
};

export interface IJobsPageProps
  extends
    IJobsPageActions,
    IJobsPageStoreProps,
    IWithPageDataProps,
    RouterProps {}

export interface IJobsPageState {
  counters: Counters;
}

const MatchingMyFilterPageWrapper: React.FC = () => {
  const context = useOutletContext<JobsPageOutletContext>();

  return <MatchingMyFilterPage {...context} />;
};

const AllOpportunitiesPageWrapper: React.FC = () => {
  const context = useOutletContext<JobsPageOutletContext>();

  return <AllOpportunitiesPage {...context} />;
};

const AllPaidPageWrapper: React.FC = () => {
  const context = useOutletContext<JobsPageOutletContext>();

  return <AllPaidPage {...context} />;
};

const ClientAlertsPageWrapper: React.FC = () => {
  const context = useOutletContext<JobsPageOutletContext>();

  return <ClientAlertsPage {...context} />;
};

const SelfTapeRequestsPageWrapper: React.FC = () => {
  const context = useOutletContext<JobsPageOutletContext>();

  return <SelfTapeRequestsPage {...context} />;
};

const MySuggestionsWrapper: React.FC = () => {
  // MySuggestions doesn't need the context props, it gets data from Redux
  return <MySuggestions />;
};

export {
  MatchingMyFilterPageWrapper,
  AllOpportunitiesPageWrapper,
  AllPaidPageWrapper,
  ClientAlertsPageWrapper,
  SelfTapeRequestsPageWrapper,
  MySuggestionsWrapper,
};

const tabToLinkMap = {
  [JobsPageTab.MyFilter]: MY_FILTERS_PATH,
  [JobsPageTab.AllPaid]: ALL_PAID_PATH,
  [JobsPageTab.All]: ALL_OPPORTUNITIES_PATH,
  [JobsPageTab.ClientAlerts]: CLIENT_ALERTS_PATH,
  [JobsPageTab.SelfTapeRequests]: SELF_TAPE_PATH,
  [JobsPageTab.MySuggestions]: MY_SUGGESTIONS_PATH,
};

const tabToHeapEventMap = {
  [JobsPageTab.MyFilter]: JobsFeedUsageEvents.TabsMatchingMyFilter,
  [JobsPageTab.AllPaid]: JobsFeedUsageEvents.TabsAllPaid,
  [JobsPageTab.All]: JobsFeedUsageEvents.TabsAllOpportunities,
};

export class JobsPage extends React.PureComponent<
  IJobsPageProps,
  IJobsPageState
> {
  public readonly state: Readonly<IJobsPageState> = {
    counters: {
      [JobsPageTab.MyFilter]: undefined,
      [JobsPageTab.AllPaid]: undefined,
      [JobsPageTab.All]: undefined,
      [JobsPageTab.ClientAlerts]: undefined,
      [JobsPageTab.SelfTapeRequests]: undefined,
      [JobsPageTab.MySuggestions]: undefined,
    },
  };

  public componentDidMount() {
    setTimeout(() => void this.loadCounters());

    this.setJobsFeedStorage(
      this.props.router.location.pathname,
      this.props.jobsFeed.pageNumber,
      this.props.jobsFeed.breakdownId,
      DEFAULT_JOBS_FEEED.IS_BACK_TO_JOBS_FEED
    );
  }

  public render() {
    const {
      user,
      commonTexts,
      locale,
      pageTranslationLocale,
      facetLocale,
      productionTypes,
      contractTypes,
      jobSettings,
      restoreScrollPosition,
      router,
    } = this.props;

    const [searchParams] = router.searchParams;
    const page = Number(searchParams.get("page")) || 1;

    const outletContext = {
      commonTexts,
      userType: user.type,
      userId: user.id,
      locale,
      pageTranslationLocale,
      facetLocale,
      productionTypes,
      contractTypes,
      jobSettings,
      refreshCounters: this.loadCounters,
      restoreScrollPosition,
      page,
      router,
    };

    return (
      <section className="c-jobs-page">
        <div>
          <BreadcrumbBar items={this.props.breadcrumbs} />
          <div className="c-jobs-page__row">
            <Tabs tabs={this.prepareTabs()} />
          </div>
          <Outlet context={outletContext} />
        </div>
      </section>
    );
  }

  private prepareTabs(): ITabProps[] {
    const { t } = i18n;
    const { tabs, router } = this.props;
    const activeTab = getLastPathnameElement(router.location.pathname);

    return tabs.map(({ id, text }): ITabProps => {
      const quantity = this.state.counters[id];

      return {
        id,
        click: () => this.handleTabClick(id),
        isActive: tabToLinkMap[id] === activeTab,
        text: t(text, {
          quantity: quantity === undefined ? "" : `(${quantity})`,
        }),
      };
    });
  }

  private readonly setJobsFeedStorage = (
    pathname: string,
    pageNumber: string,
    breakdownId: string,
    isBackToJobsFeed: boolean
  ) => {
    this.props.setJobsFeedData({
      pathname,
      pageNumber,
      breakdownId,
      isBackToJobsFeed,
    });
  };

  private handleTabClick = (tabId: JobsPageTab) => {
    const { router } = this.props;

    const pathName = `${BASE_PATH}/${tabToLinkMap[tabId]}`;

    this.setJobsFeedStorage(
      pathName,
      DEFAULT_JOBS_FEEED.PAGENUMBER,
      DEFAULT_JOBS_FEEED.BREAKDOWN_ID,
      DEFAULT_JOBS_FEEED.IS_BACK_TO_JOBS_FEED
    );

    const filterSearch = getFilterSearch(tabId);

    if (filterSearch) {
      sessionStorage.removeItem(filterSearch);
    }

    const event = tabToHeapEventMap[tabId];

    if (event) {
      sendJobsfeedUsageHeapEventWithUserPrefix(event);
    }

    router.navigate(pathName);
  };

  private loadCounters = async () => {
    const { userType } = this.props;
    const { counters } = this.state;

    if (checkIsYp()) {
      const selfTapesCounts = await this.loadSelfTapeCount();

      return this.setState({
        counters: {
          ...counters,
          [JobsPageTab.SelfTapeRequests]: selfTapesCounts,
        },
      });
    }

    try {
      const [
        [matchingMyFilterCount, allOpportunitiesCount, allPaidCount],
        clientAlertsCount,
        selfTapeCount,
        mySuggestionCount,
      ] = await Promise.all(
        _compact([
          this.loadJobsCount(),
          userType === AccountType.Agent
            ? this.loadClientAlertsCount()
            : Promise.resolve(),
          this.loadSelfTapeCount(),
          userType === AccountType.Performer && this.loadMySuggestionsCount(),
        ])
      );

      this.setState({
        counters: {
          ...counters,
          [JobsPageTab.MyFilter]: matchingMyFilterCount,
          [JobsPageTab.All]: allOpportunitiesCount,
          [JobsPageTab.AllPaid]: allPaidCount,
          [JobsPageTab.ClientAlerts]: clientAlertsCount,
          [JobsPageTab.SelfTapeRequests]: selfTapeCount,
          [JobsPageTab.MySuggestions]: mySuggestionCount,
        },
      });
    } catch (e) {
      if (!axios.isCancel(e)) {
        console.error(e);
      }
    }
  };

  private getFilterValues(
    withJobsFilterOptions: IWithJobsFilterOptions
  ): IJobFilterParams {
    const {
      commonTexts,
      productionTypes,
      contractTypes,
      jobSettings,
      userType,
    } = this.props;
    const filterConfig = setupFilterConfig({
      commonTexts,
      productionTypes,
      contractTypes,
      jobSettings,
      userType,
      ...withJobsFilterOptions,
    });
    const defaultFilterValues = {
      readStatus: JobItemReadStatus.Unread,
      pageSize: 0,
    };
    const converter = new FilterModelToJobFilterParamsConverter({
      contractTypes: filterConfig[FilterType.ContractType].facets!,
      productionTypes: filterConfig[FilterType.ProductionType].facets!,
      userType,
      jobSettings: withJobsFilterOptions?.applyUserSettings
        ? jobSettings
        : undefined,
    });

    return {
      ...converter.convert(DEFAULT_VALUES),
      ...defaultFilterValues,
    };
  }

  private async loadJobsCount() {
    const { getJobCounts, userType, abortController } = this.props;

    let counts: number[] = [];

    const allPaidFilterValues = this.getFilterValues(
      allPaidWithJobsFilterOptions
    );
    const matchingMyFilterFilterValues = this.getFilterValues(
      matchingMyFilterWithJobsFilterOptions
    );
    const allOpportunitiesFilterValues = this.getFilterValues(
      allOpportunitiesWithJobsFilterOptions
    );

    const filterSet = [
      matchingMyFilterFilterValues,
      allOpportunitiesFilterValues,
    ];

    if (userType === AccountType.Performer) {
      filterSet.push(allPaidFilterValues);
    }

    try {
      counts = await getJobCounts(filterSet, abortController);
    } catch (e) {
      console.error(e);
    }

    return counts;
  }

  private async loadClientAlertsCount() {
    const { getClientAlerts, abortController } = this.props;

    let count: number | undefined;
    const clientAlertsValues = { skip: 0, take: 0 };

    try {
      count = (await getClientAlerts(clientAlertsValues, abortController))
        .unreadTotal;
    } catch (e) {
      console.error(e);
    }

    return count;
  }

  private async loadSelfTapeCount() {
    const { getSelfTapeCount, abortController } = this.props;

    let count: number | undefined;

    try {
      count = await getSelfTapeCount(abortController);
    } catch (e) {
      console.error(e);
    }

    return count;
  }

  private async loadMySuggestionsCount() {
    try {
      return await getMySuggestionsCount();
    } catch (e) {
      console.error("error", e);
    }
  }
}

async function loadExternalData(props: IJobsPageProps) {
  return props.getFacets(
    [FacetCategory.ProductionType, FacetCategory.ContractType],
    props.locale
  );
}

const pageDataLoaders: ILoaders<IJobsPageProps> = {
  async loadTranslation(props, isLocaleChanged) {
    await props.getPageTranslation(
      { pageKey: PageKey.SelfTapeRequestNotifications, locale: props.locale },
      { throwOnError: isLocaleChanged }
    );

    return props.getPageTranslation(
      { pageKey: PageKey.Jobs, locale: props.locale },
      { throwOnError: isLocaleChanged }
    );
  },
  async loadData(props) {
    await loadExternalData(props);

    return props.getJobsPage();
  },
  loadOnLocaleChange: loadExternalData,
};

const mapStateToProps = (
  state: IApplicationState,
  ownProps: IJobsPageProps
): IJobsPageStoreProps => {
  const page = state.translation.pages[PageKey.Jobs]!;
  const pageData = state.page.data!;
  const jobSettings =
    ownProps.userType === AccountType.Performer
      ? pageData.jobSettings!.performerSettings!
      : pageData.jobSettings!.agentSettings!;

  const tabUrlSegment = getLastPathnameElement(
    ownProps.router.location.pathname
  );

  return {
    breadcrumbs: prepareBreadcrumbs(page, myAccountLink),
    tabs: prepareTabs(),
    commonTexts: prepareCommonTexts(page, ownProps.userType, tabUrlSegment),
    pageTranslationLocale: state.translation.pageLocale,
    facetLocale: state.facet.locale,
    productionTypes: state.facet[FacetStoreKey.ProductionType]!,
    contractTypes: state.facet[FacetStoreKey.ContractType]!,
    jobSettings,
    jobsFeed: state.jobsFeed,
    getJobCounts: (
      filters: IJobFilterParams[],
      abortController?: AbortController
    ) => jobService.getCounts(filters, abortController),
    getClientAlerts: (...args) => suggestionsService.getClientAlerts(...args),
    getSelfTapeCount: (...args) =>
      selfTapeRequestNotificationService.getCount(...args),
  };
};

const mapDispatchToProps: MapDispatchToProps<IJobsPageActions> = {
  getPageTranslation,
  setLoading,
  showPopup,
  hidePopup,
  getJobsPage,
  getFacets,
  setJobsFeedData,
};

export default compose(
  withRouter,
  connect(mapStateToWithPageDataProps, mapDispatchToProps),
  withPageData<IJobsPageProps>(PageKey.Jobs, pageDataLoaders),
  connect(mapStateToProps, mapDispatchToProps)
)(JobsPage);
